import { useState } from "react";

import { CATEGORY_IDS } from "../constants";

import { useLanguageContext } from "../context/LanguageContext";

import DragAndDropFile from "./ui/DragAndDropFile";
import FormInput from "./ui/FormInput";
import FormList from "./ui/FormList";
import LocationMap from "./mapRelated/LocationMap";
import ButtonFunction from "./ui/ButtonFunction";
import FormCheckbox from "./ui/FormCheckbox";

const FORM_STATE = {
  FILLLING: 1,
  LOADING: 2,
  SENDED: 3,
};

const FLAGS = {
  digital: 1, // 001 => when calculating by bits its easiear to expand and calculate
  local: 2, // 010
  personal: 4, // 100
};

function compressImage(file, options = {}) {
  const { maxWidth = 500, maxHeight = 500, quality = 0.6 } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;
    };

    img.onload = () => {
      let { width, height } = img;

      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = width * ratio;
        height = height * ratio;
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      const base64 = canvas.toDataURL("image/jpeg", quality).split(",")[1];
      resolve(base64);
    };

    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
}

export default function JoinForm({ setFormState, step, setStep }) {
  const { t } = useLanguageContext();

  const hintText = t("joinPage", "buttons", "hint");
  const nextText = t("joinPage", "buttons", "nextButton");
  const prevText = t("joinPage", "buttons", "prevButton");

  const [addressResults, setAddressResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [logoFile, setLogoFile] = useState(null);

  const [locationInfo, setLocationInfo] = useState({
    address: "",
    coords: null,
    city: "",
    province: "",
  });

  const [formData, setFormData] = useState({
    userName: "",
    userWhatsapp: "",
    businessName: "",
    category: Object.keys(CATEGORY_IDS)[0],
    businessType: null,
    tags: "",
    businessPhone: "",
    businessWebsite: "",
    businessEmail: "",
    locations: [],
    city: "",
    province: "",
    discount: "",
  });

  const [businessTypeInputs, setBusinesSTypeInputs] = useState({
    digital: false,
    local: false,
    personal: false,
  });

  function handleTypeChange(name, value) {
    setBusinesSTypeInputs((prev) => {
      const next = { ...prev, [name]: value };

      const businessType = Object.entries(next).reduce((acc, [key, val]) => (val ? acc | FLAGS[key] : acc), 0);

      handleChange("businessType", businessType || null);
      return next;
    });
  }

  function handleChange(name, value) {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  function handleDiscountChange(value) {
    if (value === "") {
      handleChange("discount", "");
      return;
    }

    if (!/^\d+$/.test(value)) return;

    const parsed = Number(value);
    if (parsed < 0 || parsed > 100) return;

    handleChange("discount", value);
  }

  function nextStep() {
    setStep((s) => Math.min(s + 1, 5));
  }

  function prevStep() {
    setStep((s) => Math.max(s - 1, 1));
  }

  function selectAddress(result) {
    const address = result.address || {};

    const city = address.city || address.town || address.village || address.municipality || "";

    const province = address.state || address.region || address.county || "";

    const road = address.road || "";
    const house = address.house_number || "";

    const fullAddress = `${city}, ${road} ${house}`.trim();

    setLocationInfo({
      address: fullAddress,
      coords: {
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon),
      },
      city,
      province,
    });

    setAddressResults([]);
  }

  function deleteLocation(index) {
    const newArray = [...formData.locations.slice(0, index), ...formData.locations.slice(index + 1)];

    setFormData((prev) => ({
      ...prev,
      locations: newArray,
    }));
  }

  function saveLocation() {
    setFormData((prev) => ({
      ...prev,
      locations: [...formData.locations, locationInfo],
    }));
    setLocationInfo({
      address: "",
      coords: null,
      city: "",
      province: "",
    });
    setStep((s) => Math.max(s - 1, 1));
  }

  async function searchAddressToCoords() {
    if (!locationInfo.address) return;

    setIsSearching(true);

    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?` + `format=json&addressdetails=1&limit=5&q=${encodeURIComponent(locationInfo.address)}`,
    );

    const data = await res.json();
    setAddressResults(data || []);
    setIsSearching(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormState(FORM_STATE.LOADING);

    let logoPayload = null;

    if (logoFile) {
      const base64 = await compressImage(logoFile, {
        maxWidth: 500,
        maxHeight: 500,
        quality: 0.7,
      });

      logoPayload = base64;
    }

    await Promise.all(
      formData.locations.map((location) => {
        const payload = {
          ...formData,
          addressText: location.address,
          city: location.city,
          province: location.province,
          coords: location.coords,
          logo: logoPayload,
        };

        return fetch("/api/submit", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
      }),
    );

    setFormState(FORM_STATE.SENDED);
  }

  return (
    <form onSubmit={handleSubmit} className="w-full h-full max-w-md pt-24 mb-6">
      {step === 1 && (
        <section className="flex flex-col gap-3 items-center">
          <h1 className="text-3xl pb-4">{t("joinPage", "AboutYou", "title")}</h1>

          <p className="text-gray-400 max-w-[35ch]">{t("joinPage", "AboutYou", "subtitle")}</p>

          <FormInput
            title={t("joinPage", "AboutYou", "input1")}
            placeholder="John Snow"
            value={formData.userName}
            onChange={(e) => handleChange("userName", e.target.value)}
          />

          <FormInput
            title={t("joinPage", "AboutYou", "input2")}
            placeholder="54 9 123 4567"
            value={formData.userWhatsapp}
            onChange={(e) => handleChange("userWhatsapp", e.target.value)}
          />

          <ButtonFunction title={nextText} func={nextStep} disabled={!formData.userName || !formData.userWhatsapp} />
        </section>
      )}

      {step === 2 && (
        <section className="flex flex-col gap-3 items-center">
          <h1 className="text-3xl pb-4">{t("joinPage", "aboutBusiness", "title")}</h1>

          <label className="py-2 block w-80" htmlFor="logoSelector">
            Logo
          </label>

          <DragAndDropFile xfile={logoFile} xsetFile={setLogoFile} />

          <FormInput
            title={t("joinPage", "aboutBusiness", "input1")}
            placeholder="Cripto Coffe"
            value={formData.businessName}
            onChange={(e) => handleChange("businessName", e.target.value)}
          />

          <FormList
            title={t("joinPage", "aboutBusiness", "input2")}
            list={Object.keys(CATEGORY_IDS).map((_, i) => t("component", "categories", i))}
            values={Object.keys(CATEGORY_IDS)}
            value={formData.category}
            onChange={(e) => handleChange("category", e.target.value)}
          />

          <h1 className="pb-1 w-80">{t("joinPage", "aboutBusiness", "input3")}</h1>
          <div className="flex flex-col gap-3">
            <FormCheckbox
              name="digital"
              title={t("component", "marker", "digitalService")}
              checked={businessTypeInputs.digital}
              onChange={handleTypeChange}
            />
            <FormCheckbox
              name="local"
              title={t("component", "marker", "localStore")}
              checked={businessTypeInputs.local}
              onChange={handleTypeChange}
            />
            <FormCheckbox
              name="personal"
              title={t("component", "marker", "physicalService")}
              checked={businessTypeInputs.personal}
              onChange={handleTypeChange}
            />
          </div>

          <FormInput
            title={t("joinPage", "aboutBusiness", "input4")}
            placeholder="Coffe, drink, chocolate"
            hint={hintText}
            value={formData.tags}
            onChange={(e) => handleChange("tags", e.target.value)}
          />

          <div className="flex w-full gap-3 mt-4">
            <ButtonFunction primary={false} title={prevText} func={prevStep} />
            <ButtonFunction title={nextText} func={nextStep} disabled={!formData.businessName || !formData.businessType} />
          </div>
        </section>
      )}

      {step === 3 && (
        <section className="flex flex-col gap-3 items-center">
          <h1 className="text-3xl pb-4 text-center">{t("joinPage", "businessInfo", "title")}</h1>

          <FormInput
            title={t("joinPage", "businessInfo", "input1")}
            placeholder="54 9 123 4567"
            hint={hintText}
            value={formData.businessPhone}
            onChange={(e) => handleChange("businessPhone", e.target.value)}
          />

          <FormInput
            title={t("joinPage", "businessInfo", "input2")}
            placeholder="criptocoffe.com"
            hint={hintText}
            value={formData.businessWebsite}
            onChange={(e) => handleChange("businessWebsite", e.target.value)}
          />

          <FormInput
            title={t("joinPage", "businessInfo", "input3")}
            placeholder="support@criptocoffe.com"
            hint={hintText}
            value={formData.businessEmail}
            onChange={(e) => handleChange("businessEmail", e.target.value)}
          />

          <FormInput
            title={t("joinPage", "businessInfo", "input4")}
            placeholder="0"
            hint={hintText}
            value={formData.discount}
            type="number"
            min={0}
            max={100}
            step={1}
            inputMode="numeric"
            pattern="[0-9]*"
            onChange={(e) => handleDiscountChange(e.target.value)}
          />

          <div className="flex w-full gap-3 mt-4">
            <ButtonFunction primary={false} title={prevText} func={prevStep} />
            <ButtonFunction title={nextText} func={nextStep} />
          </div>
        </section>
      )}

      {step === 4 && (
        <section className="flex flex-col gap-3 items-center">
          <h1 className="text-3xl pb-4 text-center">{t("joinPage", "mapLocation", "title")}</h1>

          <p className="text-gray-400 max-w-[35ch]">{t("joinPage", "mapLocation", "subtitle")}</p>

          {formData.locations.map((el, i) => (
            <article key={"location" + i} className="flex flex-between flex-row items-center border border-gray-700 w-full">
              <p className="w-full indent-3">{el.address}</p>
              <button className="p-2 px-4 hover:bg-red-500  transition duration-300" type="button" onClick={() => deleteLocation(i)}>
                x
              </button>
            </article>
          ))}

          <ButtonFunction title={t("joinPage", "mapLocation", "addLocation")} func={nextStep} />

          <div className="flex w-full gap-3 mt-4">
            <ButtonFunction primary={false} title={prevText} func={prevStep} />

            <button
              type="submit"
              disabled={!formData.locations.length > 0 || step != 4}
              className="w-1/2 py-2 rounded-lg bg-green-600 hover:bg-green-700 disabled:bg-gray-500"
            >
              {t("joinPage", "buttons", "submitButton")}
            </button>
          </div>
        </section>
      )}
      {step == 5 && (
        <section className="flex flex-col gap-3 items-center relative">
          <FormInput
            title={t("joinPage", "mapLocation", "input1")}
            value={locationInfo.address}
            onChange={(e) => {
              setLocationInfo((prev) => ({
                ...prev,
                address: e.target.value,
              }));
              setAddressResults([]);
            }}
          />

          <div className="hidden">
            <input />
          </div>

          <ButtonFunction title={t("joinPage", "buttons", "searchLocation")} func={searchAddressToCoords} />

          {isSearching && (
            <div className="w-full flex items-center justify-center gap-2 py-3 bg-gray-800 border border-gray-700 rounded-lg">
              <span className="w-4 h-4 border-2 border-main_accent border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-gray-300">{t("joinPage", "mapLocation", "searching")}</span>
            </div>
          )}

          {addressResults.length > 0 && (
            <ul className="w-full bg-gray-800 border border-gray-700 rounded-lg max-h-48 overflow-y-auto">
              {addressResults.map((result, i) => (
                <li
                  key={i}
                  onClick={() => selectAddress(result)}
                  className="px-3 py-2 text-sm text-white cursor-pointer hover:bg-gray-700 transition border-b border-gray-700"
                >
                  {result.display_name}
                </li>
              ))}
            </ul>
          )}

          <div className="w-full h-80 rounded-lg overflow-hidden border border-gray-700">
            <LocationMap
              coords={locationInfo.coords}
              onCoordsChange={(coords) =>
                setLocationInfo((prev) => ({
                  ...prev,
                  coords,
                }))
              }
            />
          </div>

          <div className="flex flex-row gap-4 w-full">
            <ButtonFunction primary={false} title={prevText} func={prevStep} />
            <ButtonFunction title={t("joinPage", "mapLocation", "saveLocation")} func={saveLocation} disabled={!locationInfo.coords} />
          </div>
        </section>
      )}
    </form>
  );
}
